import React, { useState, useEffect, useCallback } from 'react';
import { Unit } from '@types';
import UnitTitle from './UnitTitle';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { SllLesson, useMoveUnitMutation } from '@api/graphql-codegen';

type Props = {
  units: Unit[];
  lessons?: SllLesson[];
  droppableId: string;
  courseId: string;
  isPrimary?: boolean;
};

function UnitContentsSidebarList({ units, lessons, droppableId, courseId, isPrimary }: Props) {
  const [localUnits, setLocalUnits] = useState<Array<Unit>>(units);
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>();
  const [enabled, setEnabled] = useState(false);
  const [moveUnit] = useMoveUnitMutation();

  useEffect(() => {
    if (units?.length) {
      setLocalUnits(units ?? []);
    }
  }, [units]);

  const handleSelect = useCallback((id: string) => async () => {
    setSelectedUnitId(id);
  }, []);

  const handleDragEnd = useCallback(async (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const updatedUnits = [...localUnits];
    const selectedUnit = updatedUnits[result.source.index];
    delete updatedUnits[result.source.index];
    updatedUnits.splice(result.destination!.index > result.source!.index ? result.destination!.index + 1 : result.destination!.index, 0, selectedUnit);
    setLocalUnits(updatedUnits.filter(u => !!u));
    if (selectedUnit) {
      await moveUnit({
        variables: { input: {
          unitId: selectedUnit.id,
          courseId,
          primary: !!isPrimary,
          oldPosition: result.source!.index + 1,
          newPosition: result.destination!.index + 1,
        }}
      });
    }
    
  }, [courseId, isPrimary, localUnits, moveUnit]);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId} type='droppableModuleItem'>
        {(droppableProvided: DroppableProvided) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {localUnits.length > 0 ? (
              localUnits.map((item: Unit, index: number) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(
                    draggableProvided: DraggableProvided,
                    snapshot: DraggableStateSnapshot
                  ) => {
                    return (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <UnitTitle draggableProvided={draggableProvided} snapshot={snapshot} key={item.id} name={item.name} selected={selectedUnitId === item.id} onClick={handleSelect(item.id)} />
                      </div>
                    );
                  }}
                </Draggable>
              ))
            ) : (
              <div style={{ color: 'white', height: '1px' }}>{}</div>
            )}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default UnitContentsSidebarList;
